import { useState, useEffect } from 'react';
import { authApi } from '../services/api';

/**
 * Central place to resolve the currently authenticated user.
 *
 * Why not decode the JWT client-side? The backend's JWT payload only
 * contains `{sub, exp, type}` — it does NOT carry `role`, `email`, `firstName`
 * or `lastName`. Any component that used `atob(token)` to gate admin UI or
 * render the user name was silently broken (isAdmin() always returned false,
 * account panel rendered raw ObjectId). See `AccountSidebar` fix (Feb 2026).
 *
 * The hook shares an in-flight promise so multiple consumers on the same
 * page hit `/api/auth/me` at most once.
 */

let _inflight = null;
let _cache = null;
let _cachedToken = null;

const fetchMe = (token) => {
  // Invalidate cache when the token changes (login/logout on same tab).
  if (_cachedToken !== token) {
    _cache = null;
    _inflight = null;
    _cachedToken = token;
  }
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  _inflight = authApi.getMe(token)
    .then((data) => {
      _cache = data;
      _inflight = null;
      return data;
    })
    .catch((err) => {
      _inflight = null;
      throw err;
    });
  return _inflight;
};

export const invalidateCurrentUser = () => {
  _cache = null;
  _inflight = null;
  _cachedToken = null;
};

export const useCurrentUser = () => {
  const [user, setUser] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    let mounted = true;
    fetchMe(token)
      .then((data) => { if (mounted) { setUser(data); setLoading(false); } })
      .catch(() => { if (mounted) { setUser(null); setLoading(false); } });
    return () => { mounted = false; };
  }, []);

  return {
    user,
    isAdmin: user?.role === 'admin',
    isAuthenticated: !!user,
    loading,
  };
};

export default useCurrentUser;
